import React, { useEffect, useRef, useState } from "react";
import { AutoComplete, Input, Spin } from "antd";
import { EnvironmentOutlined, LoadingOutlined } from "@ant-design/icons";

const AutoCompleteAddress = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Start typing an address...",
  style = {},
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [initialized, setInitialized] = useState(false);
  const timeoutRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const googleAvailable = !!googleApiKey;

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (!googleAvailable) {
      return;
    }
    const initializeGooglePlaces = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
          const mapElement = document.createElement("div");
          placesService.current = new window.google.maps.places.PlacesService(
            mapElement,
          );
          setInitialized(true);
        } catch (e) {
          console.error("Error initializing services:", e);
        }
      }
    };

    const checkAndInitialize = () => {
      if (window.google?.maps?.places) {
        initializeGooglePlaces();
      } else {
        setTimeout(checkAndInitialize, 300);
      }
    };

    const loadGoogleMapsWithPlaces = () => {
      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com"]',
      );
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places&v=weekly`;
      script.async = false;
      script.defer = false;
      script.onerror = () => {
        console.error("Failed to load Google Maps API");
      };
      script.onload = () => {
        setTimeout(checkAndInitialize, 500);
      };
      document.head.appendChild(script);
    };

    if (window.google?.maps?.places) {
      initializeGooglePlaces();
    } else {
      loadGoogleMapsWithPlaces();
    }
  }, [googleApiKey, googleAvailable]);

  const searchAddresses = (query) => {
    if (!query || query.length < 3) {
      setOptions([]);
      return;
    }

    if (!autocompleteService.current) {
      return;
    }

    setLoading(true);

    const request = {
      input: query,
      componentRestrictions: { country: "ng" },
    };

    try {
      autocompleteService.current.getPlacePredictions(
        request,
        (predictions, status) => {
          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK &&
            status !==
              window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            setLoading(false);
            return;
          }

          if (!predictions || predictions.length === 0) {
            setOptions([]);
            setLoading(false);
            return;
          }

          const formattedOptions = predictions.map((prediction) => {
            const mainText =
              prediction.structured_formatting?.main_text ||
              prediction.main_text ||
              "";
            const secondaryText =
              prediction.structured_formatting?.secondary_text ||
              prediction.secondary_text ||
              "";

            return {
              value: prediction.description,
              label: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 0",
                  }}
                >
                  <EnvironmentOutlined
                    style={{ color: "#1B75F0", fontSize: "16px" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: "500",
                        color: "#262626",
                        fontSize: "14px",
                      }}
                    >
                      {mainText}
                    </div>
                    {secondaryText && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginTop: "2px",
                        }}
                      >
                        {secondaryText}
                      </div>
                    )}
                  </div>
                </div>
              ),
              placeId: prediction.place_id,
            };
          });

          setOptions(formattedOptions);
          setLoading(false);
        },
      );
    } catch (error) {
      setOptions([]);
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setInputValue(query);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      searchAddresses(query);
    }, 300);
  };

  const handleSelect = async (selectedValue, option) => {
    setInputValue(selectedValue);

    if (onChange) {
      onChange(selectedValue);
    }

    if (onAddressSelect && option.placeId && placesService.current) {
      try {
        const request = {
          placeId: option.placeId,
          fields: ["formatted_address", "geometry", "address_components"],
        };

        const place = await new Promise((resolve, reject) => {
          placesService.current.getDetails(request, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(place);
            } else {
              reject(new Error(status));
            }
          });
        });

        const addressComponents = {};
        if (place.address_components) {
          place.address_components.forEach((component) => {
            const types = component.types;
            if (types.includes("route")) {
              addressComponents.street = component.long_name;
            }
            if (types.includes("street_number")) {
              addressComponents.streetNumber = component.long_name;
            }
            if (types.includes("administrative_area_level_2")) {
              addressComponents.lga = component.long_name;
            }
            if (types.includes("locality") || types.includes("sublocality")) {
              addressComponents.city = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              addressComponents.state = component.long_name;
            }
            if (types.includes("country")) {
              addressComponents.country = component.long_name;
            }
            if (types.includes("postal_code")) {
              addressComponents.postcode = component.long_name;
            }
          });
        }

        const streetAddress = addressComponents.streetNumber
          ? `${addressComponents.streetNumber} ${addressComponents.street || ""}`
          : addressComponents.street || selectedValue.split(",")[0];

        const cleanAddress = [
          streetAddress,
          addressComponents.lga || addressComponents.city,
          addressComponents.state,
        ]
          .filter(Boolean)
          .join(", ");

        const { lat, lng } = place.geometry.location;

        const addressData = {
          landmark: selectedValue,
          latitude: lat(),
          longitude: lng(),
          address: streetAddress,
          street: streetAddress,
          city: addressComponents.city || "",
          lga: addressComponents.lga || addressComponents.city || "",
          state: addressComponents.state || "",
          country: addressComponents.country || "Nigeria",
          postcode: addressComponents.postcode || "",
          locality: addressComponents.city || "",
          formattedAddress: cleanAddress,
          placeName: selectedValue.split(",")[0],
          fullPlaceName: place.formatted_address,
        };

        onAddressSelect(addressData);
      } catch (error) {
        console.error("Error getting place details:", error);
      }
    }
  };

  const handleChange = (value) => {
    setInputValue(value);
    if (onChange) {
      onChange(value);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!googleAvailable) {
    console.warn("Google API key not configured. Falling back to plain input.");
    return (
      <Input
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="e.g., Near GTBank, Opposite Shoprite (Google Maps not configured)"
        style={style}
      />
    );
  }

  return (
    <AutoComplete
      value={inputValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={handleChange}
      style={{ width: "100%", ...style }}
      notFoundContent={loading ? <Spin size="small" /> : "No addresses found"}
    >
      <Input
        suffix={loading ? <LoadingOutlined /> : <EnvironmentOutlined />}
        placeholder={placeholder}
        className="py-5"
      />
    </AutoComplete>
  );
};

export default AutoCompleteAddress;
